import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { freeTools } from '../lib/freeTools';

interface FreeToolsNavProps {
  /** Desktop link styling */
  className?: string;
  /** Highlight when on a free-tool page */
  active?: boolean;
  /** Mobile: render as nested list instead of hover dropdown */
  variant?: 'desktop' | 'mobile';
  onNavigate?: () => void;
}

const FreeToolsNav: React.FC<FreeToolsNavProps> = ({
  className = '',
  active = false,
  variant = 'desktop',
  onNavigate,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (variant === 'mobile') {
    return (
      <div className='border-t border-slate-100'>
        <p className='px-4 pt-3 pb-1 font-sans text-xs font-semibold uppercase tracking-wider text-slate-500'>
          Free tools
        </p>
        {freeTools.map(tool => (
          <Link
            key={tool.slug}
            role='menuitem'
            to={tool.path}
            className='block px-4 py-2.5 font-sans text-base text-slate-700 hover:bg-slate-50 hover:text-primary-600'
            onClick={onNavigate}
          >
            {tool.label}
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className='relative'
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type='button'
        aria-expanded={open}
        aria-haspopup='menu'
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center gap-1 font-sans text-sm sm:text-base text-slate-700 hover:text-primary-600 transition-colors ${
          active ? 'font-medium text-primary-600' : ''
        } ${className}`}
      >
        Free tools
        <ChevronDown
          className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {open ? (
        <div
          role='menu'
          className='absolute left-0 top-full z-40 pt-2 w-[min(100vw-2rem,20rem)]'
        >
          <div className='overflow-hidden rounded-xl border border-slate-200/90 bg-white py-2 shadow-xl shadow-slate-900/10'>
            {freeTools.map(tool => (
              <Link
                key={tool.slug}
                role='menuitem'
                to={tool.path}
                className='block px-4 py-2.5 hover:bg-slate-50'
                onClick={() => {
                  setOpen(false);
                  onNavigate?.();
                }}
              >
                <span className='font-sans text-sm font-medium text-slate-900'>
                  {tool.label}
                </span>
                <span className='mt-0.5 block font-sans text-xs text-slate-500 leading-snug'>
                  {tool.shortDescription}
                </span>
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default FreeToolsNav;
